<script setup lang="ts">
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import BackstagePopoverPanel from '@/components/common/BackstagePopoverPanel.vue';
import { useAnalytics } from '@/composables/analytics';
import { useFormat } from '@/composables/format';
import { GaCustomEvents } from '@/models/ga-custom-events.enum';
import { Icons } from '@/models/icons.enum';
import type { NotificationMeta, NotificationModel } from '@/models/notification.model';
import { RouteNames } from '@/router';
import { NotificationService } from '@/services/notification.service';
import { useUserStore } from '@/stores/user';
import NotificationSettingsSection from '@/views/Notification/NotificationSettingsSection.vue';
import { NotificationType } from '@onrunning/backstage-models';
import { useScroll } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import HeaderTriggerButton from './HeaderTriggerButton.vue';
import NotificationCard from './NotificationCard.vue';

const showNotificationPanel = defineModel<boolean>({ required: true });

const el = ref<HTMLElement | null>(null);
const { arrivedState } = useScroll(el);
const scrollPosition = computed(() => arrivedState);
const { addCustomGaEvent } = useAnalytics();

const router = useRouter();
const route = useRoute();
const { formatDateToLocaleStringIgnoringTime } = useFormat();

const showNotificationSetting = ref(false);
const notificationSettingsPage = computed(() => showNotificationSetting.value);

const userStore = useUserStore();
const { selectedAccountId } = storeToRefs(userStore);

const notificationService = new NotificationService();

const notificationsMeta = ref({} as NotificationMeta);
const notificationItems = ref([] as NotificationModel[]);

const paginationData = reactive({
  page: 1,
  take: 5,
});

const notificationRouteMap = {
  [NotificationType.PackingSlip]: RouteNames.ViewOrder,
  [NotificationType.Payment]: RouteNames.ViewOrder,
};

const notificationIconName = computed(() => {
  return unreadNotifications.value.length > 0 ? 'bell-marked' : 'bell';
});

const unreadNotifications = computed(() => {
  return notificationItems.value.filter((notification) => notification.readAt === undefined);
});

const fetchNotifications = async () => {
  const { page, take } = paginationData;
  const { model } = await notificationService.getNotifications(selectedAccountId.value, page, take);
  if (model) {
    notificationsMeta.value = model.meta;
    // Resetting notification items when we go back to page 1, otherwise append new notifications
    if (page === 1) {
      // If it's the first page, reset the notification items to the new notifications
      notificationItems.value = model.notifications;
    } else {
      // If it's not the first page, append the new notifications to the existing ones
      notificationItems.value = [...notificationItems.value, ...model.notifications];
    }
  }
};

const markNotificationAsRead = async (notificationId: string) => {
  await notificationService.markNotificationsAsRead(selectedAccountId.value, [notificationId]);
  paginationData.page = 1;
  await fetchNotifications();
};

const onMarkAllNotificationsReadClick = async () => {
  const notificationsIds = notificationItems.value.map((notification) => notification._id);
  await notificationService.markNotificationsAsRead(selectedAccountId.value, notificationsIds);
  paginationData.page = 1;
  await fetchNotifications();
};

const sendNotificationGoogleAnalytics = async (event: GaCustomEvents) => {
  addCustomGaEvent({
    event,
  });
};

const navigateToNotification = async (notification: NotificationModel) => {
  const routeUrl = router.resolve({
    name: notificationRouteMap[notification.type],
    params: { id: notification.payload.orderNumber },
  }).href;
  window.open(routeUrl, '_blank');
};

const onNotificationClick = async (notification: NotificationModel) => {
  markNotificationAsRead(notification._id);
  sendNotificationGoogleAnalytics(GaCustomEvents.NotificationRead);
  navigateToNotification(notification);
};

const onNotificationTrigger = async () => {
  if (!showNotificationPanel.value) {
    paginationData.page = 1;
  }
  showNotificationPanel.value = true;
};

const checkScrollToBottom = () => {
  const { scrollHeight, scrollTop, clientHeight } = el.value!;
  const atBottom = scrollHeight - scrollTop === clientHeight;
  if (atBottom) {
    sendNotificationGoogleAnalytics(GaCustomEvents.NotificationScroll);
  }
};

onMounted(() => {
  el.value?.addEventListener('scroll', checkScrollToBottom);
});

onUnmounted(() => {
  el.value?.removeEventListener('scroll', checkScrollToBottom);
});

/* fetching notifications, at bottom scroll */
watch(
  () => scrollPosition.value.bottom,
  (atBottom) => {
    if (atBottom) {
      const { totalItems, take } = notificationsMeta.value;
      const possiblePages = Math.ceil(totalItems / take);

      if (possiblePages > paginationData.page) {
        paginationData.page += 1;
        fetchNotifications();
      }
    }
  },
);

/* fetching notification, on account change */
watch(
  selectedAccountId,
  async (newAccountId, oldAccountId) => {
    if (newAccountId !== oldAccountId) {
      await fetchNotifications();
    }
  },
  { immediate: true },
);

/* fetching notification, on route change to show new notifications */
watch(route, () => {
  fetchNotifications();
});
</script>

<template>
  <div class="notification">
    <BackstagePopoverPanel v-model="showNotificationPanel" @mouseleave="showNotificationPanel = false">
      <template #trigger>
        <HeaderTriggerButton
          dataCy="notification-icon"
          :iconName="notificationIconName"
          @click="onNotificationTrigger()"
          @mouseover="onNotificationTrigger()" />
      </template>

      <template #title>
        <div class="notification-title">
          <div v-if="!notificationSettingsPage" class="notification-title-main-page">
            <div>{{ $t('header.notification.title') }}</div>
            <div class="notification-title-main-page--chip" v-if="unreadNotifications.length">
              {{ $t('header.notification.chip', { unreadNumber: unreadNotifications.length }) }}
            </div>
          </div>
          <div v-if="notificationSettingsPage" class="notification-title-setting-page">
            <button class="notification-setting-return" @click.prevent="showNotificationSetting = false">
              <BackstageIcon :name="Icons.CHEVRON_LEFT" data-cy="notification-setting-back-icon" />
            </button>
            <div>{{ $t('header.notification.setting') }}</div>
          </div>
        </div>
      </template>

      <template #action>
        <button
          class="notification-mark-all-read"
          v-if="!notificationSettingsPage && notificationItems.length"
          @click="onMarkAllNotificationsReadClick()">
          {{ $t('header.notification.mark_all_as_read') }}
        </button>
        <button
          v-if="!notificationSettingsPage"
          class="notification-setting-trigger"
          @click.prevent="showNotificationSetting = true">
          <BackstageIcon name="gear" data-cy="notification-setting-icon" />
        </button>
      </template>

      <template #body>
        <div class="notification-items-page" v-if="!notificationSettingsPage" ref="el">
          <NotificationCard
            v-if="!notificationItems.length"
            :title="$t('header.notification.no_notifications_title')"
            :message="$t('header.notification.no_notifications_body')"
            :divider="false"
            :effect="false" />

          <NotificationCard
            v-for="(notification, index) in notificationItems"
            :key="notification._id"
            @click="onNotificationClick(notification)"
            :title="$t('header.notification.order_shipped')"
            :message="$t('header.notification.order_shipped_body', { orderNumber: notification.payload.orderNumber })"
            :date="formatDateToLocaleStringIgnoringTime(notification.payload.deliveryDate)"
            :unread="notification.readAt == undefined"
            :divider="!(notificationItems.length - 1 === index)" />
        </div>
        <NotificationSettingsSection v-if="notificationSettingsPage" />
      </template>
    </BackstagePopoverPanel>
  </div>
</template>

<style scoped lang="scss">
@mixin button-reset {
  border: none;
  cursor: pointer;
  background-color: transparent;
}

.notification {
  z-index: 99;

  &-title {
    display: flex;
    justify-content: space-between;
    justify-items: center;
    align-items: center;

    &-main-page {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 10px;

      &--chip {
        color: var(--shade-0);
        border-radius: 25px;
        padding: 0px 12px;
        background-color: var(--shade-6);
        font-size: 11px;
        flex-shrink: 0;
        white-space: nowrap;
      }
    }

    &-setting-page {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
  }

  &-setting-trigger {
    @include button-reset;
    padding: 0px;
    margin: 0px;
  }

  &-setting-return {
    @include button-reset;
    transform: scale(1.5);
    margin-left: -8px;
  }

  &-mark-read {
    @include button-reset;
    color: var(--shade-4);
    font-size: 12px;
    border-bottom: 1px solid transparent;
    &:hover {
      cursor: pointer;
      padding: 0px;
      border-bottom: 1px solid var(--shade-4);
    }
  }

  &-mark-all-read {
    @include button-reset;
    color: var(--primary-text-color);
    border-bottom: 1px solid transparent;
    margin-bottom: -2px;
    margin-right: 4px;
    max-width: 100px;
    &:hover {
      cursor: pointer;
      border-bottom: 1px solid var(--primary-text-color);
    }
  }

  &-items-page {
    display: flex;
    flex-direction: column;
    max-height: 425px;
    overflow-y: auto;
  }
}
</style>
